import { Avatar, Box, ButtonBase, Checkbox, Tooltip, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { preselectedReportSections, RecordSectionDescriptions } from '../constants';
import { ReportSectionComponentType } from '../types';
import { ReportSectionName, sectionNamesRequiredFna } from '../../common/types/reports';
import { useState } from 'react';
import { useUpdateReport } from '../../common/components/mutations';
import StepFooter from '../../common/components/stepFooter';

/**
 * Select sections for the report
 */
const SelectSectionsStep: ReportSectionComponentType = ({ report, onClickBack, onClickNext }) => {
  const { mutateAsync, isPending: mutationIsLoading } = useUpdateReport();
  const hasFna = report.fnAs.length > 0;
  const [selectedSectionNames, setSelectedSectionNames] = useState(
    new Set<ReportSectionName>([
      ...preselectedReportSections,
      ...report.sections.map(({ sectionName }) => sectionName),
    ]),
  );

  const handleToggleSection = (checked: boolean, SelectedSectionName: ReportSectionName): void => {
    setSelectedSectionNames((value) => {
      if (checked) {
        value.add(SelectedSectionName);
      } else {
        value.delete(SelectedSectionName);
      }
      return new Set(value);
    });
  };

  const handleClickNext = async (): Promise<void> => {
    const existingReportSectionNames = new Set(
      report.sections.map(({ sectionName }) => sectionName),
    );

    const updatedReport = await mutateAsync({
      ...report,
      sections: [
        // Filter out sections that are not selected
        ...report.sections.filter(({ sectionName }) => selectedSectionNames.has(sectionName)),
        // Add selected sections that are not already in the report
        ...[...selectedSectionNames]
          .filter((selectedSectionName) => !existingReportSectionNames.has(selectedSectionName))
          .map((selectedSectionName) => ({
            id: 0,
            sectionID: 0,
            reportID: report.id,
            sectionName: selectedSectionName,
            sectionNote: RecordSectionDescriptions[selectedSectionName] ?? '',
          })),
      ],
    });
    // Pass the updated report to the callback to use selected sections (otherwise there is race condition)
    onClickNext(updatedReport);
  };

  return (
    <>
      <Box className="space-y-4">
        {Object.values(ReportSectionName).map((sectionName) => {
          const isSelected = selectedSectionNames.has(sectionName);
          return (
            <Box key={sectionName} className="flex items-center space-x-4">
              <Box className="w-12">
                {!hasFna && sectionNamesRequiredFna.includes(sectionName) ? (
                  <Tooltip title="FNA is required for this section">
                    <Box>
                      <Checkbox disabled sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }} />
                    </Box>
                  </Tooltip>
                ) : (
                  <Checkbox
                    checked={isSelected}
                    disabled={preselectedReportSections.has(sectionName)}
                    onChange={(event): void =>
                      handleToggleSection(event.target.checked, sectionName)
                    }
                  />
                )}
              </Box>
              <ButtonBase
                sx={{ justifyContent: 'flex-start' }}
                onClick={(): void => handleToggleSection(!isSelected, sectionName)}
                className="border rounded bg-white w-full flex flex-grow p-4 space-x-4 items-center text-left justify-start"
              >
                <Avatar sx={{ bgcolor: 'background.default', color: 'text.secondary' }}>
                  <Icon width={20} icon="gis:layer-alt-o" />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={800}>
                    {sectionName}
                  </Typography>
                  <Typography color="text.secondary">
                    {RecordSectionDescriptions[sectionName]}
                  </Typography>
                </Box>
              </ButtonBase>
            </Box>
          );
        })}
      </Box>
      <StepFooter
        isLoading={mutationIsLoading}
        onClickBack={onClickBack}
        onClickNext={handleClickNext}
      />
    </>
  );
};
export default SelectSectionsStep;
